import { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'

/**
 * Pagina publica do "de acordo". Chega-se aqui pelo link de uso unico do e-mail,
 * sem login: quem tem o token e a pessoa. Por isso o token nunca aparece na tela
 * e a decisao exige um clique aqui — e-mail nao faz POST, e um GET que registrasse
 * o aceite seria disparado por qualquer prefetch de antivirus ou proxy de imagem.
 */

const API = '/api/v1/rdc/aceite'

const ROTULO: Record<string, string> = {
  A: 'Escopo padrão, já contratado',
  B: 'Funcionalidade não confirmada',
  C: 'Nova funcionalidade, melhoria ou UX/UI',
  D: 'Projeto aderente à linha de produto',
  E: 'Sem aderência à linha de produto',
}

export default function RdcAceitePage() {
  const { token = '' } = useParams()
  const [qs] = useSearchParams()
  const [dados, setDados] = useState<any>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [obs, setObs] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [feito, setFeito] = useState<null | boolean>(null)
  // A querystring do e-mail so pre-seleciona a intencao; quem decide e o clique.
  const [intencao, setIntencao] = useState<'aceito' | 'recusado'>(
    qs.get('d') === 'recusado' ? 'recusado' : 'aceito')

  const carregar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/${token}`)
      setDados(data)
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Não foi possível abrir este link.')
    }
  }, [token])

  useEffect(() => { carregar() }, [carregar])

  async function decidir() {
    setErro(null); setEnviando(true)
    try {
      await axios.post(`${API}/${token}`, {
        aceita: intencao === 'aceito',
        observacao: obs || undefined,
      })
      setFeito(intencao === 'aceito')
    } catch (e: any) {
      setErro(e?.response?.data?.detail || 'Não foi possível registrar sua resposta.')
    } finally {
      setEnviando(false)
    }
  }

  const Moldura = ({ children }: { children: any }) => (
    <div style={{ minHeight: '100vh', background: '#eceef1', padding: '32px 16px',
                  fontFamily: 'system-ui, -apple-system, Segoe UI, Arial, sans-serif' }}>
      <div style={{ maxWidth: 620, margin: '0 auto', background: '#fff', borderRadius: 10,
                    border: '1px solid #dfe3e8', overflow: 'hidden' }}>
        <div style={{ background: '#0f2740', padding: '18px 26px' }}>
          <img src="/nai-growth-mail.png" alt="Nai Growth" width={140} style={{ display: 'block' }} />
        </div>
        <div style={{ padding: 26, color: '#1c242e', lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  )

  if (erro && !dados) {
    return <Moldura>
      <h1 style={{ fontSize: 20, margin: '0 0 8px', color: '#0f2740' }}>Link indisponível</h1>
      <p style={{ color: '#41505f', margin: 0 }}>{erro}</p>
      <p style={{ color: '#8a95a1', fontSize: 13, marginTop: 16 }}>
        Links de de acordo são de uso único e expiram em 72 horas. Se o seu expirou,
        peça a quem conduziu a passagem para reabrir o pedido.
      </p>
    </Moldura>
  }

  if (!dados) {
    return <Moldura><p style={{ color: '#8a95a1', margin: 0 }}>Carregando…</p></Moldura>
  }

  if (feito !== null) {
    return <Moldura>
      <h1 style={{ fontSize: 20, margin: '0 0 8px', color: '#0f2740' }}>
        {feito ? 'De acordo registrado' : 'Ajuste solicitado'}
      </h1>
      <p style={{ color: '#41505f' }}>
        {feito
          ? 'Sua manifestação foi registrada com data, hora e origem. O RDC avança quando as duas partes tiverem respondido.'
          : 'Registramos que você não está de acordo. A passagem precisa ser retomada antes de o RDC seguir.'}
      </p>
      <p style={{ color: '#8a95a1', fontSize: 13, marginTop: 18 }}>
        Você já pode fechar esta página.
      </p>
    </Moldura>
  }

  const tipo = dados.tipo_tecnico || dados.tipo_comercial
  const Linha = ({ r, v }: { r: string; v: any }) => v ? (
    <tr>
      <td style={{ padding: '7px 0', width: 180, verticalAlign: 'top',
                   color: '#6b7785', fontSize: 13 }}>{r}</td>
      <td style={{ padding: '7px 0', verticalAlign: 'top', fontSize: 14 }}>{v}</td>
    </tr>
  ) : null

  return (
    <Moldura>
      <div style={{ fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase',
                    color: '#6b7785', marginBottom: 6 }}>
        Registro de Demanda Comercial
      </div>
      <h1 style={{ fontSize: 20, margin: '0 0 6px', color: '#0f2740' }}>
        {dados.code} — seu de acordo
      </h1>
      <p style={{ color: '#41505f', margin: '0 0 18px' }}>
        Você responde pelo lado <strong>{dados.papel === 'comercial' ? 'Comercial' : 'Tecnologia'}</strong>.
        Confirme se o entendimento abaixo é o que ficou acordado na passagem.
      </p>

      <table style={{ width: '100%', borderTop: '1px solid #dfe3e8', borderCollapse: 'collapse' }}>
        <tbody>
          <Linha r="Cliente" v={dados.empresa} />
          <Linha r="Oportunidade" v={dados.oportunidade} />
          <Linha r="Demanda do cliente" v={dados.descricao_cliente} />
          <Linha r="Entendimento acordado" v={dados.entendimento_acordado} />
          <Linha r="Plano de ações" v={dados.plano_acoes} />
          <Linha r="Tipo" v={tipo ? `${tipo} · ${ROTULO[tipo] || ''}` : null} />
        </tbody>
      </table>

      <div style={{ background: '#fef6e7', borderLeft: '3px solid #b45309',
                    padding: '12px 14px', margin: '18px 0', fontSize: 13, color: '#4a3a18' }}>
        O de acordo formaliza o entendimento e é o que passa a valer. O que não estiver
        escrito acima não está acordado.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {([['aceito', 'Estou de acordo'], ['recusado', 'Preciso de ajuste']] as const).map(([v, t]) => (
          <button key={v} onClick={() => setIntencao(v)}
            style={{ padding: '10px 18px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                     border: intencao === v ? '2px solid #0f2740' : '1px solid #dfe3e8',
                     background: intencao === v ? '#0f2740' : '#fff',
                     color: intencao === v ? '#fff' : '#41505f',
                     fontWeight: intencao === v ? 600 : 400 }}>
            {t}
          </button>
        ))}
      </div>

      <label style={{ display: 'block', fontSize: 12, textTransform: 'uppercase',
                      letterSpacing: '.06em', color: '#6b7785', marginBottom: 6 }}>
        {intencao === 'aceito' ? 'Observação (opcional)' : 'O que precisa ser ajustado'}
      </label>
      <textarea rows={3} value={obs} onChange={(e) => setObs(e.target.value)}
        style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #dfe3e8',
                 fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />

      {erro && (
        <p style={{ color: '#a11d33', fontSize: 13, marginTop: 12 }}>{erro}</p>
      )}

      <button onClick={decidir} disabled={enviando}
        style={{ marginTop: 16, padding: '12px 24px', borderRadius: 6, border: 0,
                 background: intencao === 'aceito' ? '#0f6b3f' : '#a11d33', color: '#fff',
                 fontSize: 15, fontWeight: 600, cursor: enviando ? 'default' : 'pointer',
                 opacity: enviando ? 0.6 : 1 }}>
        {enviando ? 'Registrando…'
          : intencao === 'aceito' ? 'Confirmar meu de acordo' : 'Registrar pedido de ajuste'}
      </button>

      <p style={{ color: '#8a95a1', fontSize: 12, marginTop: 16 }}>
        Sua resposta é registrada com data, hora e origem. Este link é de uso único.
      </p>
    </Moldura>
  )
}
